import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ShieldCheck, ShieldOff } from 'lucide-react';
import Button from '../ui/Button';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';

interface Props {
  enforce: boolean | null;
  onChanged: (enforce: boolean) => void;
}

/** Whether a missing equipment sign-off actually stops anyone. */
export default function InductionSettingsPanel({ enforce, onChanged }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = async (value: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const r = await inductionService.setEnforcement(value);
      onChanged(r?.enforce_equipment ?? value);
    } catch (e) {
      setError(errorMessage(e, 'Could not change the setting'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 text-sm text-neutral-700">
      <section className="space-y-3">
        <h3 className="font-semibold text-neutral-900">Equipment sign-offs</h3>
        {enforce === null ? (
          <p className="text-neutral-500">Loading…</p>
        ) : (
          <div className={`rounded-lg border p-3 sm:p-4 ${enforce ? 'border-success-200 bg-success-50' : 'border-warning-200 bg-warning-50'}`}>
            <div className="flex items-start gap-2 min-w-0">
              {enforce
                ? <ShieldCheck className="h-5 w-5 shrink-0 text-success-700" />
                : <ShieldOff className="h-5 w-5 shrink-0 text-warning-700" />}
              <div className="min-w-0 break-words">
                <p className="font-semibold text-neutral-900">{enforce ? 'Enforced' : 'Not enforced'}</p>
                {enforce ? (
                  <p className="mt-1">
                    Nobody can be put on a job with a machine, or pre-start a machine, unless they hold a current
                    sign-off for it. A supervisor can sign someone off on the spot; anyone else is left off the job and a
                    request comes here.
                  </p>
                ) : (
                  <p className="mt-1">
                    Sign-offs are recorded but nothing is stopped. Anyone can be put on any machine. Use this only while
                    you record your existing operators.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {enforce
                ? <Button size="sm" variant="secondary" onClick={() => set(false)} isLoading={saving}>Stop enforcing</Button>
                : <Button size="sm" onClick={() => set(true)} isLoading={saving}>Enforce sign-offs</Button>}
            </div>
          </div>
        )}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 break-words">{error}</div>}
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-neutral-900">Licences and tickets</h3>
        <p className="break-words">
          Licences (forklift, chemical handling, first aid) live on the Staff Certifications register, not here. An
          induction can list the certifications it needs, and can refuse anyone who does not hold them — a missing
          licence cannot be overridden on the spot.
        </p>
        <Link to="/certifications/admin" className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline">
          <ExternalLink className="h-4 w-4 shrink-0" /> Staff Certifications
        </Link>
      </section>
    </div>
  );
}
